import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useRef, useState } from "react";
import { SECONDARY_BUTTON_SX } from "@/constants/buttons";

interface FilterDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  formatOptionLabel?: (option: string) => string;
}

export default function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  formatOptionLabel,
}: FilterDropdownProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const isActive = selected.length > 0;

  return (
    <>
      <Button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(true)}
        startIcon={
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-label="Filter">
            <path
              d="M13.3332 4.6665H7.33317M9.33317 11.3332H3.33317M9.33317 11.3332C9.33317 12.4377 10.2286 13.3332 11.3332 13.3332C12.4377 13.3332 13.3332 12.4377 13.3332 11.3332C13.3332 10.2286 12.4377 9.33317 11.3332 9.33317C10.2286 9.33317 9.33317 10.2286 9.33317 11.3332ZM6.6665 4.6665C6.6665 5.77107 5.77107 6.6665 4.6665 6.6665C3.56193 6.6665 2.6665 5.77107 2.6665 4.6665C2.6665 3.56193 3.56193 2.6665 4.6665 2.6665C5.77107 2.6665 6.6665 3.56193 6.6665 4.6665Z"
              stroke="var(--color-primary-active)"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
        sx={{
          ...SECONDARY_BUTTON_SX,
          borderColor: isActive ? "var(--color-primary-active)" : "var(--color-rodeo-dust)",
          backgroundColor: "white",
          borderRadius: "6px",
          height: "40px",
          minWidth: "120px",
          px: 2,
          whiteSpace: "nowrap",
          "&:hover": {
            backgroundColor: "white",
            borderColor: "var(--color-primary-active)",
          },
        }}
      >
        {label}
        {selected.length > 0 ? ` (${selected.length})` : ""}
      </Button>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              minWidth: 230,
              maxHeight: 400,
              border: "1px solid var(--color-rodeo-dust)",
              borderRadius: "6px",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => onToggle(option)}
            dense
            sx={{
              px: 2,
              py: "6px",
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <Checkbox
              checked={selected.includes(option)}
              size="small"
              disableRipple
              sx={{
                p: 0,
                mr: 1.25,
                color: "var(--color-rodeo-dust)",
                "&.Mui-checked": { color: "var(--color-primary-active)" },
              }}
            />
            <ListItemText
              primary={formatOptionLabel ? formatOptionLabel(option) : option}
              slotProps={{
                primary: {
                  style: {
                    fontSize: "14px",
                    color: "var(--color-text-primary)",
                  },
                },
              }}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0 }} />

        <MenuItem
          onClick={() => {
            onClear();
            setOpen(false);
          }}
          sx={{
            justifyContent: "center",
            py: "10px",
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          <ListItemText
            primary="Clear filters"
            slotProps={{
              primary: {
                style: {
                  fontSize: "14px",
                  color: "#6D6D6D",
                  textAlign: "center",
                },
              },
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
